import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { formatDate } from "../utils/formatDate";
import {
  FileJson,
  ChevronLeft,
  ChevronRight,
  Upload,
  Brain,
  Eye,
  Trash2,
  RefreshCw,
  X,
  File,
  CheckCircle,
  AlertCircle,
  Clock,
  Building2,
} from "lucide-react";
import { cobGetWorkOrdersV3, cobGetStatsV3, cobExtractV3, cobDeleteWorkOrderV3 } from "../api";
import "./COBWorkOrdersList.css";

// Custom Dropdown Component for Status Filter
const CustomStatusDropdown = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const options = [
    { value: "all", label: "All Status" },
    { value: "pending", label: "Pending" },
    { value: "processing", label: "Processing" },
    { value: "completed", label: "Completed" },
    { value: "failed", label: "Failed" },
  ];

  const selectedOption = options.find((opt) => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  return (
    <div className="cob-custom-dropdown" ref={dropdownRef}>
      <button className="cob-dropdown-trigger" onClick={() => setIsOpen(!isOpen)}>
        <span>{selectedOption?.label}</span>
        <svg className={`dropdown-arrow ${isOpen ? "open" : ""}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </button>
      {isOpen && (
        <div className="cob-dropdown-menu">
          {options.map((option) => (
            <button
              key={option.value}
              className={`cob-dropdown-option ${value === option.value ? "selected" : ""}`}
              onClick={() => { onChange(option.value); setIsOpen(false); }}
            >
              {option.label}
              {value === option.value && <span className="check-mark">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Upload Modal Component
const UploadModal = ({ isOpen, onClose, onUploadComplete }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState({});
  const [uploadResults, setUploadResults] = useState([]);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  const modelConfig = {
    name: "Change Of Bank",
    description: "AWS Bedrock - Full Page Analysis",
    color: "#7c3aed",
  };

  const handleFileSelect = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    addFiles(selectedFiles);
  };

  const addFiles = (newFiles) => {
    const validFiles = newFiles.filter(f => 
      f.name.toLowerCase().endsWith('.pdf') ||
      f.name.toLowerCase().endsWith('.tiff') ||
      f.name.toLowerCase().endsWith('.tif') ||
      f.name.toLowerCase().endsWith('.jpg') ||
      f.name.toLowerCase().endsWith('.jpeg') ||
      f.name.toLowerCase().endsWith('.png')
    );
    const existingNames = files.map(f => f.name);
    const uniqueFiles = validFiles.filter(f => !existingNames.includes(f.name));
    
    if (validFiles.length !== newFiles.length) {
      alert('Only PDF, TIFF, JPG, PNG files are supported. Other files were skipped.');
    }
    
    setFiles(prev => [...prev, ...uniqueFiles]);
    
    const newStatus = {};
    uniqueFiles.forEach(f => {
      newStatus[f.name] = 'pending';
    });
    setUploadStatus(prev => ({ ...prev, ...newStatus }));
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dropZoneRef.current?.classList.remove('drag-over');
    
    const droppedFiles = Array.from(event.dataTransfer.files);
    addFiles(droppedFiles);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dropZoneRef.current?.classList.add('drag-over');
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dropZoneRef.current?.classList.remove('drag-over');
  };

  const removeFile = (filename) => {
    setFiles(prev => prev.filter(f => f.name !== filename));
    setUploadStatus(prev => {
      const newStatus = { ...prev };
      delete newStatus[filename];
      return newStatus;
    });
  };

  const handleUploadAll = async () => {
    if (files.length === 0) return;
    
    setUploading(true);
    const results = [];

    for (const file of files) {
      setUploadStatus(prev => ({ ...prev, [file.name]: 'uploading' }));
      
      try {
        const result = await cobExtractV3(file);
        setUploadStatus(prev => ({ ...prev, [file.name]: 'success' }));
        results.push({ filename: file.name, success: true, work_order_id: result.work_order_id || result._id });
      } catch (err) {
        setUploadStatus(prev => ({ ...prev, [file.name]: 'error' }));
        results.push({ filename: file.name, success: false, error: err.message });
      }
    }

    setUploadResults(results);
    setUploading(false);
    
    if (onUploadComplete) {
      onUploadComplete(results);
    }
  };

  const handleClose = () => {
    setFiles([]);
    setUploadStatus({});
    setUploadResults([]);
    onClose();
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'uploading':
        return <RefreshCw size={16} className="spin" style={{ color: '#3b82f6' }} />;
      case 'success':
        return <CheckCircle size={16} style={{ color: '#22c55e' }} />;
      case 'error':
        return <AlertCircle size={16} style={{ color: '#ef4444' }} />;
      default:
        return <Clock size={16} style={{ color: '#9ca3af' }} />;
    }
  };

  if (!isOpen) return null;

  const allUploaded = files.length > 0 && files.every(f => uploadStatus[f.name] === 'success' || uploadStatus[f.name] === 'error');
  const successCount = Object.values(uploadStatus).filter(s => s === 'success').length;
  const errorCount = Object.values(uploadStatus).filter(s => s === 'error').length;

  return (
    <div className="cob-modal-overlay" onClick={handleClose}>
      <div className="cob-upload-modal" onClick={e => e.stopPropagation()}>
        <div className="cob-modal-header" style={{ borderBottomColor: modelConfig.color }}>
          <h3>
            <Upload size={20} style={{ color: modelConfig.color }} />
            Upload Files - {modelConfig.name}
          </h3>
          <button className="cob-modal-close" onClick={handleClose}>
            <X size={20} />
          </button>
        </div>

        <div className="cob-modal-body">
          <div
            ref={dropZoneRef}
            className="cob-drop-zone"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload size={48} style={{ color: modelConfig.color }} />
            <p>Drag & drop files here</p>
            <span>or click to browse</span>
            <span className="cob-drop-hint">PDF, TIFF, JPG, PNG (max 50MB)</span>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.tiff,.tif,.jpg,.jpeg,.png"
              multiple
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </div>

          {files.length > 0 && (
            <div className="cob-file-list">
              <div className="cob-file-list-header">
                <span>{files.length} file(s) selected</span>
                {allUploaded && (
                  <span className="cob-upload-summary">
                    {successCount} success, {errorCount} failed
                  </span>
                )}
              </div>
              <div className="cob-file-items">
                {files.map(file => (
                  <div key={file.name} className={`cob-file-item ${uploadStatus[file.name]}`}>
                    <div className="cob-file-info">
                      <File size={16} />
                      <span className="cob-file-name" title={file.name}>
                        {file.name.length > 40 ? file.name.substring(0, 40) + '...' : file.name}
                      </span>
                      <span className="cob-file-size">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </span>
                    </div>
                    {uploadStatus[file.name] === 'error' && uploadResults.find(r => r.filename === file.name)?.error && (
                      <div className="cob-file-error">
                        {uploadResults.find(r => r.filename === file.name)?.error}
                      </div>
                    )}
                    <div className="cob-file-actions">
                      {getStatusIcon(uploadStatus[file.name])}
                      {uploadStatus[file.name] === 'pending' && !uploading && (
                        <button 
                          className="cob-file-remove"
                          onClick={() => removeFile(file.name)}
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="cob-modal-footer">
          <button className="cob-btn-cancel" onClick={handleClose}>
            {allUploaded ? 'Close' : 'Cancel'}
          </button>
          {!allUploaded && (
            <button
              className="cob-btn-upload-all"
              style={{ backgroundColor: modelConfig.color }}
              onClick={handleUploadAll}
              disabled={files.length === 0 || uploading}
            >
              {uploading ? (
                <>
                  <RefreshCw size={16} className="spin" />
                  Extracting...
                </>
              ) : (
                <>
                  <Upload size={16} />
                  Extract {files.length} File{files.length !== 1 ? 's' : ''}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const COBWorkOrdersV3 = () => {
  const navigate = useNavigate();

  const [workOrders, setWorkOrders] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0, page: 1, pages: 1, limit: 10, skip: 0,
  });
  const [stats, setStats] = useState({
    total: 0, completed: 0, processing: 0, pending: 0, failed: 0,
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [error, setError] = useState("");
  const [showUploadModal, setShowUploadModal] = useState(false);
  
  const searchTimerRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async (skip = 0, search = searchTerm) => {
    try {
      setLoading(true);
      setError("");

      const [ordersRes, statsRes] = await Promise.all([
        cobGetWorkOrdersV3({
          skip,
          limit: 10,
          status: statusFilter !== "all" ? statusFilter : undefined,
          search: search || undefined,
        }),
        cobGetStatsV3(),
      ]);

      if (ordersRes.work_orders || ordersRes.data) {
        const orders = ordersRes.work_orders || ordersRes.data || [];
        setWorkOrders(orders);
        setPagination(ordersRes.pagination || { 
          total: orders.length, 
          page: Math.floor(skip / 10) + 1, 
          pages: Math.ceil((ordersRes.total || orders.length) / 10), 
          limit: 10, 
          skip 
        });
      }

      if (statsRes) {
        setStats({
          total: statsRes.total_work_orders || statsRes.total || 0,
          completed: statsRes.by_status?.completed || statsRes.completed || 0,
          processing: statsRes.by_status?.processing || statsRes.processing || 0,
          pending: statsRes.by_status?.pending || statsRes.pending || 0,
          failed: statsRes.by_status?.failed || statsRes.failed || 0,
        });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }
    
    searchTimerRef.current = setTimeout(() => {
      fetchData(0, value);
    }, 500);
  };

  const handleUploadComplete = (results) => {
    fetchData(0);
    
    const successResults = results.filter(r => r.success);
    if (successResults.length === 1 && successResults[0].work_order_id) {
      setShowUploadModal(false);
      navigate(`/cob/work-order/${successResults[0].work_order_id}`);
    }
  };

  const handleDelete = async (workOrderId, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this work order?")) return;

    try {
      await cobDeleteWorkOrderV3(workOrderId);
      fetchData(pagination.skip);
    } catch (err) {
      setError(`Delete failed: ${err.message}`);
    }
  };

  const handleNextPage = () => {
    if (pagination.page < pagination.pages) {
      fetchData(pagination.skip + pagination.limit);
    }
  };

  const handlePrevPage = () => {
    if (pagination.page > 1) {
      fetchData(Math.max(0, pagination.skip - pagination.limit));
    }
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      completed: "status-completed",
      processing: "status-processing",
      pending: "status-pending",
      failed: "status-failed",
    };
    return classes[status] || "status-pending";
  };

  // Helper to get bank match info from extracted data
  const getBankMatchInfo = (wo) => {
    const result = wo.extracted_data || wo.result;
    if (!result) return "-";
    
    // Check if we have old_bank and new_bank sections
    if (result.old_bank || result.new_bank) {
      const oldBank = result.old_bank?.bank_name || "Old";
      const newBank = result.new_bank?.bank_name || "New";
      return `${oldBank.substring(0, 10)}... → ${newBank.substring(0, 10)}...`;
    }
    
    // Check for form_bank_details
    if (result.form_bank_details) {
      return result.form_bank_details.bank_name?.substring(0, 20) || "-";
    }
    
    return "-";
  };

  return (
    <div className="cob-work-orders-list">
      {/* Header with Model Info */}
      <div className="cob-header-banner" style={{ borderLeftColor: "#7c3aed" }}>
        <div className="cob-header-info">
          <h2 style={{ color: "#7c3aed" }}>
            <Brain size={24} />
            Change Of Bank
          </h2>
          <p>AWS Bedrock - Full Page Bank Change Analysis</p>
        </div>
        <div className="cob-header-actions">
          <button
            className="cob-btn-upload"
            onClick={() => setShowUploadModal(true)}
            style={{ backgroundColor: "#7c3aed" }}
          >
            <Upload size={18} /> Upload & Extract
          </button>
        </div>
      </div>

      {error && (
        <div className="cob-error-banner">
          {error}
          <button onClick={() => setError("")}>×</button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="cob-stats-grid">
        <div className="cob-stat-card-sm">
          <div className="cob-stat-value">{stats.total}</div>
          <div className="cob-stat-label">Total</div>
        </div>
        <div className="cob-stat-card-sm completed">
          <div className="cob-stat-value">{stats.completed}</div>
          <div className="cob-stat-label">Completed</div>
        </div>
        <div className="cob-stat-card-sm processing">
          <div className="cob-stat-value">{stats.processing}</div>
          <div className="cob-stat-label">Processing</div>
        </div>
        <div className="cob-stat-card-sm pending">
          <div className="cob-stat-value">{stats.pending}</div>
          <div className="cob-stat-label">Pending</div>
        </div>
        <div className="cob-stat-card-sm failed">
          <div className="cob-stat-value">{stats.failed}</div>
          <div className="cob-stat-label">Failed</div>
        </div>
      </div>

      {/* Filters */}
      <div className="cob-filters-row">
        <input
          type="text"
          placeholder="Search by ID, filename, or UID..."
          className="cob-search-input"
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <CustomStatusDropdown value={statusFilter} onChange={setStatusFilter} />
        <button className="cob-btn-refresh" onClick={() => fetchData(0)}>
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      {/* Table */}
      <div className="cob-table-container">
        <table className="cob-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Filename</th>
              <th>Version</th>
              <th>Status</th>
              <th>Bank Info</th>
              <th>Created</th>
              <th>Duration</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" className="cob-table-loading">
                  <RefreshCw size={20} className="spin" /> Loading...
                </td>
              </tr>
            ) : workOrders.length === 0 ? (
              <tr>
                <td colSpan="8" className="cob-table-empty">
                  No work orders found. Upload a Change of Bank form to get started.
                </td>
              </tr>
            ) : (
              workOrders.map((wo) => (
                <tr key={wo._id} onClick={() => navigate(`/cob/work-order/${wo._id}`)}>
                  <td className="cob-id-cell">{wo._id?.slice(-8)}</td>
                  <td className="cob-filename-cell" title={wo.filename}>
                    {wo.filename?.substring(0, 30) || wo.uid || "-"}
                    {wo.filename?.length > 30 && "..."}
                  </td>
                  <td>
                    <span
                      className="cob-version-badge"
                      style={{
                        backgroundColor: "#f3e8ff",
                        color: "#7c3aed",
                      }}
                    >
                      v3 - Bedrock
                    </span>
                  </td>
                  <td>
                    <span className={`cob-status-badge ${getStatusBadgeClass(wo.status)}`}>
                      {wo.status}
                    </span>
                  </td>
                  <td className="cob-bank-info-cell" title={getBankMatchInfo(wo)}>
                    <Building2 size={14} style={{ marginRight: '4px', opacity: 0.6 }} />
                    {getBankMatchInfo(wo)}
                  </td>
                  <td>{formatDate(wo.created_at)}</td>
                  <td>{wo.processing_time ? `${wo.processing_time.toFixed(1)}s` : "-"}</td>
                  <td className="cob-actions-cell" onClick={(e) => e.stopPropagation()}>
                    <button
                      className="cob-action-btn view"
                      title="View Details"
                      onClick={() => navigate(`/cob/work-order/${wo._id}`)}
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      className="cob-action-btn json"
                      title="Download JSON"
                      onClick={(e) => {
                        e.stopPropagation();
                        const data = wo.extracted_data || wo.result || wo;
                        const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `${wo.filename?.replace(/\.[^/.]+$/, "") || wo._id}.json`;
                        a.click();
                      }}
                    >
                      <FileJson size={16} />
                    </button>
                    <button
                      className="cob-action-btn delete"
                      title="Delete"
                      onClick={(e) => handleDelete(wo._id, e)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="cob-pagination">
        <div className="cob-pagination-info">
          Showing {workOrders.length > 0 ? pagination.skip + 1 : 0} - {Math.min(pagination.skip + pagination.limit, pagination.total)} of {pagination.total}
        </div>
        <div className="cob-pagination-controls">
          <button onClick={handlePrevPage} disabled={pagination.page <= 1}>
            <ChevronLeft size={16} /> Previous
          </button>
          <span className="cob-page-indicator">
            Page {pagination.page} of {pagination.pages || 1}
          </span>
          <button onClick={handleNextPage} disabled={pagination.page >= pagination.pages}>
            Next <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* Upload Modal */}
      <UploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadComplete={handleUploadComplete}
      />
    </div>
  );
};

export default COBWorkOrdersV3;
